// La vista previa del Agente compilado (Process-Compiled Agent, Wave 2 — W2-5),
// y desde W4'-3b el PUBLICAR que la escribe.
//
// `getAgentPreview` es un GET y nada más: de sólo lectura, no publica, no
// escribe y no aplica nada. Se vuelve a pedir sola cada vez que cambian los
// procesos, las etapas o la ficha, porque quien las guarda invalida
// `agent-compiled-preview`.
//
// DEBAJO viven las tres llamadas del Apply, que es la ola 4 ocupando el lugar
// que esta cabecera le tenía guardado: la vista previa del publish (qué
// cambiaría y qué lo impide), el publish y el deshacer. Las tres devuelven
// frases YA REDACTADAS por el servidor, que es el que escribe: la pantalla las
// pinta tal cual y no compone ninguna.
//
// Los tests de la pantalla mockean este módulo con una fábrica EXHAUSTIVA, así
// que un export nuevo hay que agregarlo en cada una de ellas: si no, se queda
// sin mockear en silencio y la suite dispara axios de verdad.

import type {
  AgentCategorySource,
  AgentDelta,
  AgentTerminal,
  CompilerStageBehavior,
} from '@vysto/shared/compiler'

import { apiClient } from '../../api/client.js'

const enc = encodeURIComponent

/** Una pregunta de la captura de un proceso, tal como el agente la haría. */
export interface PreviewQuestion {
  key: string
  label: string
  /** El tipo de la ficha, para etiquetarlo con `DATA_TYPE_LABELS`. `null` en la
   *  pregunta del nombre, que no sale del catálogo. */
  dataType: string | null
}

/** Una pregunta de una etapa CON LA FRASE QUE EL AGENTE DICE. `questions` dice
 *  QUÉ datos se piden; esto dice CÓMO se piden, con el `prompt` que el
 *  compilador ya escribió en el nodo `ask`. */
export interface PreviewAsk {
  key: string
  label: string
  prompt: string
  dataType: string | null
  /** DE DÓNDE SALE SU LISTA, ya redactado por el servidor (D21-3). La pantalla
   *  no compone esta frase: es la MISMA que el recibo del Publicar dice, y
   *  componerla aquí sería la segunda redacción del mismo hecho. `null` cuando
   *  la lista la escribe el cliente, o cuando el dato no es de lista. */
  sourceNote: string | null
}

/** Una ETAPA del proceso, como la vista previa la dibuja (F2-6). El proceso
 *  entero, no sólo lo que se compila: `compiles` es la frontera honesta. */
export interface PreviewStage {
  key: string
  name: string
  behavior: CompilerStageBehavior
  /** Lo que la etapa EXIGE para entrar, en etiquetas del cliente. */
  requires: string[]
  /** Lo que la etapa declara que OBTIENE, también en etiquetas. */
  obtains: string[]
  /** ¿Esta etapa baja a algo que el agente corre? */
  compiles: boolean
  asks: PreviewAsk[]
  /** Quién atiende la etapa. Una captura que atiende una PERSONA entrega la
   *  conversación entera al llegar, así que la tarjeta lo avisa. */
  owner: 'bot' | 'member' | 'team'
}

export interface PreviewProcess {
  key: string
  name: string
  optionLabel: string
  questions: PreviewQuestion[]
  terminal: AgentTerminal
  /** El proceso COMPLETO, en el orden total del compilador. */
  stages: PreviewStage[]
  /** El mensaje con el que la captura cierra, palabra por palabra. '' cuando
   *  ninguna etapa de captura compila. */
  confirmText: string
  /** Ninguna etapa de captura declaró un dato, así que la captura pregunta la
   *  ficha COMPLETA en la primera. La tarjeta lo dice una vez, arriba del
   *  proceso: si no, se lee como que el compilador ignoró las columnas. */
  wholeFichaFallback: boolean
}

/** Un turno de la conversación de ejemplo. `kind` es lo que impide que el
 *  ejemplo mienta: sólo `says` es una frase que el agente de verdad dice;
 *  `intent` y `answers` DESCRIBEN lo que la persona quiere o contesta, y
 *  `event` es la terminal de la rama, que viaja como su CLAVE. */
export interface PreviewTurn {
  speaker: 'agent' | 'person'
  kind: 'says' | 'intent' | 'answers' | 'event'
  text: string
}

export interface PreviewExampleProcess {
  key: string
  turns: PreviewTurn[]
}

export interface PreviewExample {
  processes: PreviewExampleProcess[]
}

/** Una opción BASE del menú: lo que el agente ya sabe atender sin que salga de
 *  un proceso del espacio (los presets del giro). */
export interface PreviewCategory {
  key: string
  label: string
  source: AgentCategorySource
}

/** D6: la etapa virtual del consentimiento, con lo que de ella SÍ se configura.
 *  La puerta siempre corre y su lugar no se mueve; el modo, la versión y el
 *  aviso son del cliente y se editan en Configuración → Privacidad. */
export interface PreviewConsent {
  name: string
  /** Valores del DOMINIO: la pantalla los traduce con las MISMAS palabras que
   *  usa Privacidad, para que el cliente lea dos veces lo mismo. */
  acceptanceMode: 'implicit' | 'affirmative'
  termsVersion: string
  usingOwnNotice: boolean
  /** El principio del mensaje que la persona recibe. '' cuando no hay ninguno. */
  noticeExcerpt: string
}

/** La identidad de canal de la que salió el menú de esta vista previa. */
export interface PreviewChannelIdentity {
  id: string
  channel: string
  channelLabel: string
  displayName: string
}

export interface AgentPreview {
  greeting: string
  /** D6: la etapa virtual del consentimiento. Se dibuja PRIMERA y no se compila:
   *  quien la ejecuta es la puerta del pipeline, no el flujo. */
  consentStage: PreviewConsent | null
  processes: PreviewProcess[]
  categories: PreviewCategory[]
  pendingCaptures: number
  /** De qué canal es esta vista previa, y cuántos atiende el agente. Con más de
   *  uno la pantalla lo dice: el menú cuelga de la identidad, no del agente. */
  channelIdentity: PreviewChannelIdentity | null
  channelIdentityCount: number
  /** Cómo se leería una conversación con este agente, proceso por proceso.
   *  Derivada SÓLO de lo compilado: si el flujo no contiene la frase, el
   *  ejemplo no la dice. */
  example: PreviewExample
}

export interface AgentPreviewResult {
  preview: AgentPreview
  /** Las frases las redacta el SERVIDOR, con el mismo cálculo que hizo la
   *  comparación: componerlas aquí sería una segunda redacción del mismo hecho. */
  delta: AgentDelta
  /** Ningún espacio tiene conexiones configuradas todavía, así que la vista
   *  previa asume "todos los procesos, en su orden". Se dice, para no presentar
   *  una suposición como un hecho configurado. */
  synthesizedConnections: boolean
}

interface PreviewPayload {
  preview: {
    greeting: string
    consent_stage: {
      name: string
      acceptance_mode: 'implicit' | 'affirmative'
      terms_version: string
      using_own_notice: boolean
      notice_excerpt: string
    } | null
    channel_identity: {
      id: string
      channel: string
      channel_label: string
      display_name: string
    } | null
    channel_identity_count: number
    processes: {
      key: string
      name: string
      option_label: string
      questions: { key: string; label: string; data_type: string | null }[]
      terminal: AgentTerminal
      stages: {
        key: string
        name: string
        behavior: CompilerStageBehavior
        requires: string[]
        obtains: string[]
        compiles: boolean
        asks: {
          key: string
          label: string
          prompt: string
          data_type: string | null
          source_note: string | null
        }[]
        owner: 'bot' | 'member' | 'team'
      }[]
      confirm_text: string
      whole_ficha_fallback: boolean
    }[]
    categories: { key: string; label: string; source: AgentCategorySource }[]
    pending_captures: number
    example: {
      processes: {
        key: string
        turns: {
          speaker: 'agent' | 'person'
          kind: 'says' | 'intent' | 'answers' | 'event'
          text: string
        }[]
      }[]
    }
  }
  delta: AgentDelta
  meta: { synthesized_connections: boolean }
}

/** GET — no escribe nada y no publica nada. Quien la vuelve a pedir es la
 *  pantalla que guarda procesos, etapas o ficha, invalidando
 *  `agent-compiled-preview`: la vista previa no tiene botón de actualizar
 *  porque nunca hace falta uno. */
export async function getAgentPreview(botId: string): Promise<AgentPreviewResult> {
  const res = await apiClient.get<{ data: PreviewPayload }>(
    `/me/bots/${enc(botId)}/compiled-preview`,
  )
  const d = res.data.data
  return {
    preview: {
      greeting: d.preview.greeting,
      consentStage:
        d.preview.consent_stage === null
          ? null
          : {
              name: d.preview.consent_stage.name,
              acceptanceMode: d.preview.consent_stage.acceptance_mode,
              termsVersion: d.preview.consent_stage.terms_version,
              usingOwnNotice: d.preview.consent_stage.using_own_notice,
              noticeExcerpt: d.preview.consent_stage.notice_excerpt,
            },
      processes: d.preview.processes.map((p) => ({
        key: p.key,
        name: p.name,
        optionLabel: p.option_label,
        questions: p.questions.map((q) => ({
          key: q.key,
          label: q.label,
          dataType: q.data_type,
        })),
        terminal: p.terminal,
        stages: p.stages.map((s) => ({
          key: s.key,
          name: s.name,
          behavior: s.behavior,
          requires: s.requires,
          obtains: s.obtains,
          compiles: s.compiles,
          asks: s.asks.map((a) => ({
            key: a.key,
            label: a.label,
            prompt: a.prompt,
            dataType: a.data_type,
            sourceNote: a.source_note ?? null,
          })),
          owner: s.owner,
        })),
        confirmText: p.confirm_text,
        wholeFichaFallback: p.whole_ficha_fallback,
      })),
      categories: d.preview.categories.map((c) => ({
        key: c.key,
        label: c.label,
        source: c.source,
      })),
      pendingCaptures: d.preview.pending_captures,
      channelIdentity:
        d.preview.channel_identity === null
          ? null
          : {
              id: d.preview.channel_identity.id,
              channel: d.preview.channel_identity.channel,
              channelLabel: d.preview.channel_identity.channel_label,
              displayName: d.preview.channel_identity.display_name,
            },
      channelIdentityCount: d.preview.channel_identity_count,
      example: {
        processes: d.preview.example.processes.map((p) => ({
          key: p.key,
          turns: p.turns.map((t) => ({ speaker: t.speaker, kind: t.kind, text: t.text })),
        })),
      },
    },
    delta: d.delta,
    synthesizedConnections: d.meta.synthesized_connections,
  }
}

// ─── Publicar el agente entero (W4'-3b, el Apply del ciclo) ─────────────────

/** Una razón por la que NO se puede publicar, ya redactada por el servidor.
 *  `code` es lo que se reconoce sin leer español; `message` es lo que se pinta. */
export interface PublishBlocker {
  code: string
  message: string
}

export interface PublishPreviewResult {
  /** Lo que cambiaría, en las frases del servidor. El MISMO cálculo con el que
   *  va a escribir: componerlo aquí sería una segunda redacción del mismo hecho. */
  delta: AgentDelta
  /** TODAS las razones que lo impiden. El POST refusa con la primera; la
   *  pantalla las enseña enteras, porque quien mira quiere saber qué le falta. */
  blockers: PublishBlocker[]
  /** El canal por el que este agente conversa, dicho por su nombre: de él cuelga
   *  el menú que se publicaría. `null` cuando no hay ninguno conectado. */
  identity: { label: string } | null
  /** La versión del flujo principal que se leyó. Viaja de vuelta en el POST para
   *  que publicar no sobrescriba en silencio lo que otro guardó mientras tanto. */
  expectedVersion: number | null
}

export interface PublishAgentResult {
  compilationId: string
  /** El recibo, renglón por renglón y ya redactado. */
  facts: string[]
  delta: AgentDelta
  /** A qué conjunto vuelve deshacer. `null` cuando ésta fue la primera
   *  publicación: deshacerla devuelve el flujo principal de antes de compilar. */
  undo: { compilationId: string | null }
}

/** GET — sin efectos. Dice lo que pasaría y todo lo que lo impide antes de que
 *  nadie apriete nada, con el mismo cálculo que corre el POST. */
export async function getPublishPreview(botId: string): Promise<PublishPreviewResult> {
  const res = await apiClient.get<{
    data: {
      delta: AgentDelta
      blockers: PublishBlocker[]
      identity: { label: string } | null
      expected_version: number | null
    }
  }>(`/me/bots/${enc(botId)}/publish-agent/preview`)
  const d = res.data.data
  return {
    delta: d.delta,
    blockers: d.blockers,
    identity: d.identity,
    expectedVersion: d.expected_version,
  }
}

/** Publica el agente ENTERO como un conjunto. Un 409 trae la razón, ya
 *  redactada: el primer impedimento, o la carrera con quien publicó mientras
 *  tanto. `expectedVersion` es lo que la vista previa leyó. */
export async function publishAgent(
  botId: string,
  expectedVersion?: number | null,
): Promise<PublishAgentResult> {
  const res = await apiClient.post<{
    data: {
      compilation_id: string
      facts: string[]
      delta: AgentDelta
      undo: { compilation_id: string | null }
    }
  }>(
    `/me/bots/${enc(botId)}/publish-agent`,
    expectedVersion === null || expectedVersion === undefined
      ? {}
      : { expected_version: expectedVersion },
  )
  const d = res.data.data
  return {
    compilationId: d.compilation_id,
    facts: d.facts,
    delta: d.delta,
    undo: { compilationId: d.undo.compilation_id },
  }
}

// ─── El probador de ruteo (W4'-7) ───────────────────────────────────────────

/** A qué entraría una frase, contestado por el clasificador REAL sobre el menú
 *  compilado. `sentence` viene YA REDACTADA por el servidor y la pantalla la
 *  pinta tal cual — la misma disciplina que el resto del módulo; los campos
 *  estructurados quedan para la interfaz que venga. */
export interface RouteTestResult {
  intent: string
  kind: 'proceso' | 'opcion_base' | 'ninguno'
  label: string | null
  processName: string | null
  sentence: string
}

/** POST — no escribe nada: clasifica la frase con el mismo mecanismo que una
 *  conversación real y contesta a qué proceso u opción base entraría. Una
 *  llamada al LLM por cada Probar; sin caché en v1. */
export async function routeTest(botId: string, text: string): Promise<RouteTestResult> {
  const res = await apiClient.post<{
    data: {
      intent: string
      kind: 'proceso' | 'opcion_base' | 'ninguno'
      label: string | null
      process_name: string | null
      sentence: string
    }
  }>(`/me/bots/${enc(botId)}/route-test`, { text })
  const d = res.data.data
  return {
    intent: d.intent,
    kind: d.kind,
    label: d.label,
    processName: d.process_name,
    sentence: d.sentence,
  }
}

/** Deshace la publicación, HACIA ADELANTE: republica el conjunto anterior como
 *  versiones nuevas. Devuelve la frase del SERVIDOR, por la misma razón que la
 *  vista previa: si la pantalla redactara su propio "ya lo deshicimos", el día
 *  que el servidor deshaga algo distinto seguiríamos diciendo lo mismo. */
export async function undoPublish(botId: string, compilationId: string): Promise<string> {
  const res = await apiClient.post<{ data: { ok: true; summary: string } }>(
    `/me/bots/${enc(botId)}/publish-agent/undo`,
    { compilation_id: compilationId },
  )
  return res.data.data.summary
}
